import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { S } from '../regureStyles';

// Attach-to-Client modal. Symmetric with LinkToClaim so the Document Processing
// screen can offer either target (or both) depending on the tenant's config.
// Nothing here is tenant-specific — the UI reads whatever clients the current
// tenant has and lets the operator pick one.
const LinkToClient = ({
  selected,
  linkClientId,
  setLinkClientId,
  setSelected,
  handleLinkClient,
  linking,
  availableClients,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!Array.isArray(availableClients)) return [];
    const q = query.trim().toLowerCase();
    if (!q) return availableClients.slice(0, 25);
    return availableClients
      .filter(c => {
        const name = (c.displayName || c.companyName || [c.firstName, c.lastName].filter(Boolean).join(' ') || '').toLowerCase();
        const num  = (c.clientNumber || '').toLowerCase();
        return name.includes(q) || num.includes(q);
      })
      .slice(0, 25);
  }, [availableClients, query]);

  if (!selected) return <></>;

  const displayNameFor = (c) => c.displayName || c.companyName || [c.firstName, c.lastName].filter(Boolean).join(' ') || t('screens.unmatchedDocs.clientPicker.unnamed', { defaultValue: '(unnamed)' });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: 520, boxShadow: '0 25px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>
          {t('screens.unmatchedDocs.clientPicker.title', { defaultValue: 'Attach Document to Client' })}
        </div>
        <div style={{ fontSize: 13, color: S.textMuted, marginBottom: 20 }}>
          <strong>{selected.filename || selected.originalName}</strong>
          {selected.documentType ? <> — {selected.documentType}</> : null}
        </div>

        <label style={{ fontSize: 13, fontWeight: 500, color: S.textSecondary, display: 'block', marginBottom: 6 }}>
          {t('screens.unmatchedDocs.clientPicker.searchLabel', { defaultValue: 'Search clients' })}
        </label>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={t('screens.unmatchedDocs.clientPicker.searchPh', { defaultValue: 'Name or client number…' })}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 8,
            fontSize: 14,
            outline: 'none',
            boxSizing: 'border-box',
            marginBottom: 14,
          }}
        />

        <div style={{
          maxHeight: 260,
          overflowY: 'auto',
          border: `1px solid ${S.borderLight}`,
          borderRadius: 8,
          marginBottom: 20,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: S.textMuted }}>
              {availableClients.length === 0
                ? t('screens.unmatchedDocs.clientPicker.noneAvailable', { defaultValue: 'No clients found for this tenant.' })
                : t('screens.unmatchedDocs.clientPicker.noneMatching', { defaultValue: 'No matches.' })}
            </div>
          ) : filtered.map(c => {
            const active = linkClientId === c._id;
            return (
              <button
                key={c._id}
                onClick={() => setLinkClientId(c._id)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: active ? '#fef9c3' : 'transparent',
                  border: 'none',
                  borderBottom: `1px solid ${S.borderLight}`,
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{displayNameFor(c)}</div>
                <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>
                  {c.clientNumber || '(no number)'} · {c.type || '—'}
                  {c.address?.city ? ` · ${c.address.city}${c.address.state ? ', ' + c.address.state : ''}` : ''}
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={() => {
              setSelected(null);
              setLinkClientId('');
              setQuery('');
            }}
            style={{
              padding: '10px 20px',
              border: `1px solid ${S.borderLight}`,
              borderRadius: 8,
              background: S.bgCard,
              fontSize: 13,
              cursor: 'pointer',
              color: S.textSecondary,
            }}
          >
            {t('screens.unmatchedDocs.clientPicker.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={() => handleLinkClient(selected._id)}
            disabled={!linkClientId || linking}
            style={{
              padding: '10px 20px',
              background: !linkClientId ? '#94a3b8' : `linear-gradient(135deg, ${S.burgundy}, #722f37)`,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: !linkClientId ? 'not-allowed' : 'pointer',
            }}
          >
            {linking
              ? t('screens.unmatchedDocs.clientPicker.attaching', { defaultValue: 'Attaching…' })
              : t('screens.unmatchedDocs.clientPicker.attach', { defaultValue: 'Attach' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkToClient;
